// @ts-check
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import {
	clearActiveExtractionJobId,
	closeSnackbar,
	getFiles,
	getJob,
	getJobProgress,
	restoreCad2ImageProgress,
	updateExtractProgress,
} from "../../../../actions";
import { snackbarTypeConstants } from "../../../../constants/constants";
import { getSocket } from "../../../../utils/web-socket";
import localize, { LocKeys } from "../../../../constants/localizations";
import useProgressRestoration from "../../../hooks/useProgressRestoration";
import { isExtractionInitiator } from "../../../../reducers/globalWidgetReducer";
import {
	extractProgressEventSchema,
	ipServerLabelingEventSchema,
} from "../../../../schemas/socket";
import { validateResponse } from "../../../../schemas/validate";

/**
 * @typedef {Object} ExtractProgressContext
 * @property {number|string|null|undefined} jobId
 * @property {number|string|null|undefined} disciplineId
 * @property {number|string|null|undefined} buildingId
 * @property {Array<{ id: number|string, level?: string }>} buildingLevels
 * @property {number} tempProgress
 * @property {number} totalLevels
 * @property {boolean} isBimifyExtraction
 */

/**
 * @typedef {Object} ExtractProgressStatusData
 * @property {number|string} [id]
 * @property {number|string} [jobDisciplineBuildingLevelFileId]
 * @property {number|string} [percentage]
 * @property {string} [message]
 * @property {boolean} [isIpServer]
 * @property {boolean} [isError]
 * @property {boolean} [error]
 * @property {boolean} [isReady]
 * @property {number|string} [currentStep]
 * @property {number|string} [totalSteps]
 */

/**
 * @typedef {Object} ExtractProgressEffects
 * @property {boolean} closeExtract
 * @property {boolean} closeBimify
 * @property {boolean} refreshJob
 * @property {boolean} refreshFiles
 */

/**
 * @typedef {Object} ExtractProgressUpdate
 * @property {Object} data
 * @property {number} data.progress
 * @property {string} data.message
 * @property {boolean} data.inProgress
 * @property {number|string|null|undefined} data.jobId
 * @property {number|string|null|undefined} data.disciplineId
 * @property {number|string|null|undefined} data.buildingId
 * @property {boolean} data.errorResponse
 * @property {boolean} data.successResponse
 * @property {Array<{ id: number|string, level?: string }>} data.buildingLevels
 * @property {number} data.tempProgress
 * @property {number} data.totalLevels
 * @property {boolean} data.isBimifyExtraction
 * @property {ExtractProgressEffects} effects
 */

/**
 * @typedef {ExtractProgressUpdate["data"]} ExtractProgressUpdateData
 */

/**
 * @typedef {Object} ExtractionProgressListenerProps
 * @property {number|string|null|undefined} jobId
 * @property {Array<{ id: number|string, level?: string }>} buildingLevels
 * @property {number|string|null|undefined} disciplineId
 * @property {number} tempProgress
 * @property {number} totalLevels
 * @property {number|string|null|undefined} buildingId
 * @property {boolean} isBimifyExtraction
 * @property {(id: number, prefillJobTemp?: boolean, step?: number, cb?: number, controller?: any) => Promise<void>} getJob
 * @property {(payload: { buildingId: number|string|null|undefined, disciplineId: number|string|null|undefined, usePagination: boolean, showNormalised: boolean, isForAllFiles?: boolean }) => void} getFiles
 * @property {(type: string) => void} closeSnackbar
 * @property {(data: ExtractProgressUpdateData) => void} updateExtractProgress
 * @property {(params: { jobId: number, cb: Function }) => Function} getJobProgress
 * @property {(data: Object) => { type: string, data: Object }} restoreCad2ImageProgress
 */

/**
 * @typedef {Pick<ExtractionProgressListenerProps,
 * "jobId" | "buildingLevels" | "disciplineId" | "tempProgress" | "totalLevels" | "buildingId" | "isBimifyExtraction">}
 * ExtractionProgressListenerStateProps
 */

/**
 * Pure helper that translates websocket payload + current contextual state into an update object
 * and a set of side-effects to perform (closing snackbars, refreshing job/files).
 * Returned shape: { data, effects }
 * @param {ExtractProgressStatusData} statusData
 * @param {ExtractProgressContext} ctx
 * @returns {ExtractProgressUpdate}
 */
export const buildExtractProgressUpdate = (statusData, ctx) => {
	const {
		jobId,
		disciplineId = null,
		buildingId = null,
		buildingLevels = [],
		tempProgress = 0,
		totalLevels = 1,
		isBimifyExtraction = false,
	} = ctx;

	const {
		id: jobDisciplineBuildingLevelFileId,
		percentage = 0,
		message = "",
		isIpServer = false,
		isError = false,
		error = false,
		isReady = false,
		currentStep,
		totalSteps,
	} = statusData || {};

	// Upstream PC/PTS worker uses the legacy `error` key; IP Server uses `isError`.
	// Treat either as an error signal.
	const hasError = Boolean(isError) || Boolean(error);

	const effects = {
		closeExtract: false,
		closeBimify: false,
		refreshJob: false,
		refreshFiles: false,
	};

	const base = {
		progress: Number(percentage) || 0,
		message,
		inProgress: true,
		jobId,
		disciplineId,
		buildingId,
		errorResponse: false,
		successResponse: false,
		buildingLevels,
		tempProgress,
		totalLevels,
		isBimifyExtraction,
	};

	// Server (level-by-level) extraction path
	if (isIpServer) {
		if (jobDisciplineBuildingLevelFileId != null) {
			const findLevel = buildingLevels.find(
				(l) => +l.id === +jobDisciplineBuildingLevelFileId
			);
			if (findLevel) {
				base.message = `${findLevel.level} - ${message}`;
			}
		}

		const denominator = Math.max(1, Number(totalLevels));
		let computedProgress =
			Number(tempProgress) + Number(percentage) / denominator;

		// Cap interim aggregated progress at 90 until final readiness conditions
		if (computedProgress >= 90) computedProgress = 90;

		base.progress = computedProgress;

		if (
			Number(percentage) === 100 &&
			jobDisciplineBuildingLevelFileId != null
		) {
			// Remove finished level from tracking
			const remaining = buildingLevels.filter(
				(l) => +l.id !== +jobDisciplineBuildingLevelFileId
			);
			// Always update progress tracking, don't close prematurely.
			// The widget should stay open until the final isReady message.
			base.tempProgress = base.progress;
			base.buildingLevels = remaining;
		}

		// Individual level completion (currentStep === totalSteps) - do NOT close widget.
		// Only refresh job/files when the last level completes to avoid unnecessary requests.
		// Intermediate level completions don't produce useful data changes worth fetching.
		if (
			Number(percentage) === 100 &&
			currentStep &&
			totalSteps &&
			currentStep === totalSteps &&
			base.buildingLevels.length === 0
		) {
			effects.refreshJob = true;
			effects.refreshFiles = true;
		}
	} else if (statusData?.jobDisciplineBuildingLevelFileId != null) {
		// Non-maverick IP Server per-level processing (no isIpServer flag).
		// These messages arrive after C2I when shouldUseMaverick is false.
		// Show real per-level progress instead of staying stuck at 10%.
		const fileId = String(statusData.jobDisciplineBuildingLevelFileId);

		// Show level name in message
		const findLevel = buildingLevels.find((l) => String(l.id) === fileId);
		if (findLevel) {
			base.message = `${findLevel.level} - ${message}`;
		}

		// Compute aggregated progress across levels.
		// Range: 10% (C2I base) → 90% (cap before labeling finalization).
		// Each remaining level gets an equal share of the available range.
		const baseOffset = Math.max(Number(tempProgress), 10);
		const remainingCount = Math.max(buildingLevels.length, 1);
		const perLevelRange = (90 - baseOffset) / remainingCount;
		let computedProgress =
			baseOffset + (Number(percentage) / 100) * perLevelRange;

		if (computedProgress >= 90) computedProgress = 90;
		base.progress = computedProgress;

		// When a level completes, remove from tracking and save progress
		if (Number(percentage) === 100) {
			base.buildingLevels = buildingLevels.filter(
				(l) => String(l.id) !== fileId
			);
			base.tempProgress = computedProgress;

			// Refresh job/files when the last level completes
			if (base.buildingLevels.length === 0) {
				effects.refreshJob = true;
				effects.refreshFiles = true;
			}
		}

		// Transition out of C2I phase so subsequent messages
		// (e.g. isReady) don't fall back to the 10% branch.
		base.isBimifyExtraction = false;
	} else if (isBimifyExtraction) {
		// C2I serverless phase: keep progress fixed at 10% to avoid jumpy
		// percentages from parallel multi-level processing. Show the latest
		// message from the serverless function (includes level name).
		base.progress = 10;
		base.tempProgress = 10;
		if (Number(percentage) === 100) {
			// C2I complete — IP Server will start automatically
			base.message = localize(LocKeys.EXTRACTION_COMPLETED_WAITING_FOR_SERVER);
			effects.refreshJob = true;
			effects.refreshFiles = true;
		}
	} else {
		// Non-IP server path (legacy fallback)
		if (Number(percentage) === 100) {
			base.progress = 100;
			base.message = localize(LocKeys.EXTRACTION_COMPLETED_WAITING_FOR_SERVER);
			// Keep snackbar open (legacy behaviour) but refresh job/files
			effects.refreshJob = true;
			effects.refreshFiles = true;
		}
	}

	// Error state overrides everything else
	if (hasError) {
		base.progress = 100;
		base.inProgress = false;
		base.errorResponse = true;
		effects.closeExtract = true;
		effects.closeBimify = true; // legacy: also close bimify progress on extraction error
	}

	// Ready (success) state
	// Only close the widget when IPServer sends final isReady AND all levels are done.
	// Each level sends its own isReady when finished, but the widget should stay open
	// until the last level completes (base.buildingLevels is already filtered above).
	if (isReady && isIpServer) {
		// PC Maverick fires a single aggregate ready event whose `id` is the PC
		// processing job id, not a jobDisciplineBuildingLevelFile id, so the
		// per-level filter above never empties buildingLevels. When currentStep
		// equals totalSteps, treat it as job-wide completion and clear levels.
		const isJobWideComplete =
			currentStep != null &&
			totalSteps != null &&
			Number(currentStep) === Number(totalSteps);
		if (isJobWideComplete) {
			base.buildingLevels = [];
			base.tempProgress = 100;
		}
		if (base.buildingLevels.length === 0) {
			base.progress = 100;
			base.inProgress = false;
			base.successResponse = true;
			effects.closeExtract = true;
		}
		effects.refreshJob = true;
		effects.refreshFiles = true;
	} else if (isReady && !isIpServer) {
		// Non-maverick: job completed but labeling events will follow.
		// Hold at 90% until ip-server-labeling events close the widget.
		base.progress = 90;
		base.message = localize(LocKeys.EXTRACTION_COMPLETED_WAITING_FOR_SERVER);
		effects.refreshJob = true;
		effects.refreshFiles = true;
	}

	return { data: base, effects };
};

/**
 * @param {ExtractionProgressListenerProps} props
 */
const ExtractionProgressListener = (props) => {
	const {
		// state
		buildingId,
		jobId,
		disciplineId,
		tempProgress,
		totalLevels,
		buildingLevels,
		isBimifyExtraction,
		activeExtractionJobId,
		// actions
		getJob,
		closeSnackbar,
		updateExtractProgress,
		getFiles,
		getJobProgress,
		restoreCad2ImageProgress,
		clearActiveExtractionJobId,
	} = props;

	// Keep latest activeExtractionJobId in a ref so the socket listener
	// (subscribed once per jobId) reads the current gate without re-subscribing.
	const activeExtractionJobIdRef = useRef(activeExtractionJobId);
	useEffect(() => {
		activeExtractionJobIdRef.current = activeExtractionJobId;
	}, [activeExtractionJobId]);

	// Fetch progress from API on mount + re-fetch on WebSocket reconnect
	useProgressRestoration(jobId ?? null, {
		getJobProgress,
		restoreCad2ImageProgress,
	});

	/** @type {React.MutableRefObject<ExtractProgressContext>} */
	const ctxRef = useRef({
		jobId,
		disciplineId,
		buildingId,
		buildingLevels: Array.isArray(buildingLevels) ? buildingLevels : [],
		tempProgress,
		totalLevels,
		isBimifyExtraction,
	});

	// Keep latest values without re-subscribing the socket listener
	useEffect(() => {
		ctxRef.current = {
			jobId,
			disciplineId,
			buildingId,
			buildingLevels: Array.isArray(buildingLevels) ? buildingLevels : [],
			tempProgress,
			totalLevels,
			isBimifyExtraction,
		};
	}, [
		jobId,
		disciplineId,
		buildingId,
		buildingLevels,
		tempProgress,
		totalLevels,
		isBimifyExtraction,
	]);

	// Track jobDisciplineBuildingLevelFileId values seen during progress events
	// so we know which ip-server-labeling-{fileId} events to wait for.
	/** @type {React.MutableRefObject<Set<string>>} */
	const levelFileIdsRef = useRef(new Set());

	// When isReady arrives without isIpServer (non-maverick), we need to wait
	// for ip-server-labeling events before closing the widget.
	const [awaitingLabeling, setAwaitingLabeling] = useState(false);

	useEffect(() => {
		if (!jobId) return; // Nothing to subscribe to

		const socket = getSocket();
		if (!socket.connected) socket.connect(); // ensure connection
		const event = `job-status-read-pc-${jobId}`;

		/**
		 * @param {ExtractProgressStatusData} statusData
		 */
		const listener = (statusData) => {
			validateResponse(
				extractProgressEventSchema,
				statusData,
				"socket:extract-progress"
			);
			const { data, effects } = buildExtractProgressUpdate(
				statusData,
				ctxRef.current
			);

			// Track file IDs for non-maverick labeling events
			if (statusData.jobDisciplineBuildingLevelFileId != null) {
				levelFileIdsRef.current.add(
					String(statusData.jobDisciplineBuildingLevelFileId)
				);
			}

			// When isReady arrives without isIpServer, wait for labeling events
			if (
				statusData.isReady &&
				!statusData.isIpServer &&
				levelFileIdsRef.current.size > 0
			) {
				setAwaitingLabeling(true);
			}

			// Maverick (isIpServer) path: ip-server-labeling-{fileId} is the
			// only per-level completion signal — Maverick never emits a
			// per-level 100% progress event. Seed levelFileIdsRef from the
			// canonical buildingLevels list (the by-event tracking above may
			// only have the first level when this fires) and start awaiting.
			if (
				statusData.isIpServer &&
				statusData.jobDisciplineBuildingLevelFileId != null
			) {
				const seedLevels = ctxRef.current.buildingLevels;
				if (Array.isArray(seedLevels)) {
					seedLevels.forEach((l) => {
						if (l?.id != null) {
							levelFileIdsRef.current.add(String(l.id));
						}
					});
				}
				setAwaitingLabeling(true);
			}

			// Dispatch the progress update first — every tab's widget UI rehydrates.
			updateExtractProgress(data);

			// Initiator-gated side effects: only the tab that clicked BIMIFY/Verify
			// runs job/file refreshes and clears the gate. Other tabs receive the
			// same broadcast but must not duplicate work.
			const isInitiator = isExtractionInitiator(
				activeExtractionJobIdRef.current,
				ctxRef.current.jobId
			);

			if (isInitiator && effects.refreshJob) {
				const ctxJobId = ctxRef.current.jobId;
				const jobIdNumber = ctxJobId != null ? Number(ctxJobId) : null;
				if (jobIdNumber != null && !Number.isNaN(jobIdNumber)) {
					getJob(jobIdNumber, true, 2);
				}
			}
			if (isInitiator && effects.refreshFiles) {
				// Refuse to call /file/get-all without scope. After a hard reload
				// mid-extraction the redux scope can be null even though the
				// initiator gate is restored from sessionStorage; an unscoped
				// request would return every file in the system.
				const { buildingId: bId, disciplineId: dId } = ctxRef.current;
				if (bId != null || dId != null) {
					// isForAllFiles: true targets the `allFilesList_*` redux slot
					// that FooterInputView and ExtractModal read from. Without it
					// the dispatch only updates `filesList_*` and the file picker
					// never shows the newly produced PNGs.
					getFiles({
						buildingId: bId,
						disciplineId: dId,
						usePagination: false,
						showNormalised: true,
						isForAllFiles: true,
					});
				}
			}
			// closeSnackbar is pure UI — every tab converges its widget.
			if (effects.closeExtract) {
				closeSnackbar(snackbarTypeConstants.EXTRACT_PROGRESS);
				if (isInitiator) {
					clearActiveExtractionJobId();
				}
			}
			if (effects.closeBimify) {
				closeSnackbar(snackbarTypeConstants.BIMIFY_PROGRESS);
			}
		};

		socket.on(event, listener);
		return () => {
			socket.off(event, listener);
			levelFileIdsRef.current.clear();
			setAwaitingLabeling(false);
			// Do not disconnect here; socket is shared across the app.
		};
	}, [
		jobId,
		getJob,
		getFiles,
		closeSnackbar,
		updateExtractProgress,
		clearActiveExtractionJobId,
	]);

	// Subscribe to ip-server-labeling-{fileId} events once the job signals
	// isReady without isIpServer (non-maverick extraction).
	// Close the progress widget after all tracked files report isDxfpFinished.
	useEffect(() => {
		if (!awaitingLabeling) return;

		const socket = getSocket();
		const fileIds = [...levelFileIdsRef.current];
		if (fileIds.length === 0) return;

		const remaining = new Set(fileIds);

		const subscriptions = fileIds.map((fileId) => {
			const event = `ip-server-labeling-${fileId}`;
			const handler = (data) => {
				validateResponse(
					ipServerLabelingEventSchema,
					data,
					"socket:ip-server-labeling"
				);

				const isMaverickDone = Boolean(
					data?.isMavericFinished || data?.isPcMavericFinished
				);
				const isDxfpDone = Boolean(data?.isDxfpFinished);

				if (!isMaverickDone && !isDxfpDone) return;
				if (!remaining.has(fileId)) return;

				remaining.delete(fileId);

				// Maverick path: emit incremental aggregated progress as each
				// level finishes. Without this the bar would stay at whatever
				// the last per-level progress event showed (max ~25%) until
				// the very end.
				if (isMaverickDone && remaining.size > 0) {
					const ctx = ctxRef.current;
					const total = Math.max(1, Number(ctx.totalLevels));
					const completedCount = total - remaining.size;
					const aggregated = Math.min(90, (completedCount / total) * 100);
					const nextLevels = (ctx.buildingLevels || []).filter((l) =>
						remaining.has(String(l.id))
					);
					updateExtractProgress({
						progress: aggregated,
						inProgress: true,
						successResponse: false,
						errorResponse: false,
						jobId: ctx.jobId,
						disciplineId: ctx.disciplineId,
						buildingId: ctx.buildingId,
						buildingLevels: nextLevels,
						tempProgress: aggregated,
						totalLevels: ctx.totalLevels,
						isBimifyExtraction: ctx.isBimifyExtraction,
						message: "",
					});
				}

				if (remaining.size === 0) {
					// Close the widget directly — updateExtractProgressLabelingTool
					// relies on buildingLevels being empty, but in the non-isIpServer
					// path levels are never removed. Instead, match the isIpServer+isReady
					// close path: mark success, close snackbar, refresh data.
					updateExtractProgress({
						progress: 100,
						inProgress: false,
						successResponse: true,
						errorResponse: false,
						jobId: ctxRef.current.jobId,
						disciplineId: ctxRef.current.disciplineId,
						buildingId: ctxRef.current.buildingId,
						buildingLevels: [],
						tempProgress: 0,
						totalLevels: ctxRef.current.totalLevels,
						isBimifyExtraction: ctxRef.current.isBimifyExtraction,
						message: "",
					});
					closeSnackbar(snackbarTypeConstants.EXTRACT_PROGRESS);

					const isInitiator = isExtractionInitiator(
						activeExtractionJobIdRef.current,
						ctxRef.current.jobId
					);
					if (isInitiator) {
						const ctxJobId = ctxRef.current.jobId;
						const jobIdNumber = ctxJobId != null ? Number(ctxJobId) : null;
						if (jobIdNumber != null && !Number.isNaN(jobIdNumber)) {
							getJob(jobIdNumber, true, 2);
						}
						const { buildingId: bId, disciplineId: dId } = ctxRef.current;
						if (bId != null || dId != null) {
							// isForAllFiles: true — see comment on the
							// equivalent call in the main listener above.
							getFiles({
								buildingId: bId,
								disciplineId: dId,
								usePagination: false,
								showNormalised: true,
								isForAllFiles: true,
							});
						}
						clearActiveExtractionJobId();
					}
					setAwaitingLabeling(false);
					levelFileIdsRef.current.clear();
				}
			};
			socket.on(event, handler);
			return { event, handler };
		});

		return () => {
			subscriptions.forEach(({ event, handler }) => socket.off(event, handler));
		};
	}, [
		awaitingLabeling,
		updateExtractProgress,
		closeSnackbar,
		getJob,
		getFiles,
		clearActiveExtractionJobId,
	]);

	// Backstop for the Maverick path: if one or more per-level
	// ip-server-labeling-{fileId} events fail to fire (e.g. a level crashed
	// before the labeling write), the aggregate maverick-serverless-completed
	// event closes the widget so it doesn't hang indefinitely.
	useEffect(() => {
		if (!awaitingLabeling) return;
		if (!jobId) return;

		const socket = getSocket();
		const event = `maverick-serverless-completed-${jobId}`;

		const handler = () => {
			updateExtractProgress({
				progress: 100,
				inProgress: false,
				successResponse: true,
				errorResponse: false,
				jobId: ctxRef.current.jobId,
				disciplineId: ctxRef.current.disciplineId,
				buildingId: ctxRef.current.buildingId,
				buildingLevels: [],
				tempProgress: 0,
				totalLevels: ctxRef.current.totalLevels,
				isBimifyExtraction: ctxRef.current.isBimifyExtraction,
				message: "",
			});
			closeSnackbar(snackbarTypeConstants.EXTRACT_PROGRESS);

			const isInitiator = isExtractionInitiator(
				activeExtractionJobIdRef.current,
				ctxRef.current.jobId
			);
			if (isInitiator) {
				const ctxJobId = ctxRef.current.jobId;
				const jobIdNumber = ctxJobId != null ? Number(ctxJobId) : null;
				if (jobIdNumber != null && !Number.isNaN(jobIdNumber)) {
					getJob(jobIdNumber, true, 2);
				}
				const { buildingId: bId, disciplineId: dId } = ctxRef.current;
				if (bId != null || dId != null) {
					// isForAllFiles: true — see comment on the equivalent call
					// in the main listener above.
					getFiles({
						buildingId: bId,
						disciplineId: dId,
						usePagination: false,
						showNormalised: true,
						isForAllFiles: true,
					});
				}
				clearActiveExtractionJobId();
			}
			setAwaitingLabeling(false);
			levelFileIdsRef.current.clear();
		};

		socket.on(event, handler);
		return () => {
			socket.off(event, handler);
		};
	}, [
		awaitingLabeling,
		jobId,
		updateExtractProgress,
		closeSnackbar,
		getJob,
		getFiles,
		clearActiveExtractionJobId,
	]);

	return <div />; // visual responsibility handled by snackbar wrapper
};

/**
 * @param {import("immutable").Map<string, unknown>} state
 */
const mapStateToProps = (state) =>
	/** @type {ExtractionProgressListenerStateProps} */ ({
		jobId: /** @type {number|string|null|undefined} */ (
			state.getIn(["globalWidget", "extractProgress", "jobId"])
		),
		buildingLevels:
			/** @type {Array<{ id: number|string, level?: string }>} */ (
				state.getIn(["globalWidget", "extractProgress", "buildingLevels"])
			),
		disciplineId: /** @type {number|string|null|undefined} */ (
			state.getIn(["globalWidget", "extractProgress", "disciplineId"])
		),
		tempProgress: /** @type {number} */ (
			state.getIn(["globalWidget", "extractProgress", "tempProgress"])
		),
		totalLevels: /** @type {number} */ (
			state.getIn(["globalWidget", "extractProgress", "totalLevels"])
		),
		buildingId: /** @type {number|string|null|undefined} */ (
			state.getIn(["globalWidget", "extractProgress", "buildingId"])
		),
		isBimifyExtraction: /** @type {boolean} */ (
			state.getIn(["globalWidget", "extractProgress", "isBimifyExtraction"])
		),
		activeExtractionJobId: /** @type {number|string|null|undefined} */ (
			state.getIn(["globalWidget", "activeExtractionJobId"])
		),
	});

export default connect(mapStateToProps, {
	getJob,
	updateExtractProgress,
	closeSnackbar,
	getFiles,
	getJobProgress,
	restoreCad2ImageProgress,
	clearActiveExtractionJobId,
})(ExtractionProgressListener);
